import { GroupedNotification } from "@/pages/rollshop/models/RollshopNotification";
import { HelmetDexteel } from "@dexteel/mesf-core";
import { ExpandMore } from "@mui/icons-material";
import {
	Box,
	Card,
	CardContent,
	Chip,
	Collapse,
	Grid2,
	IconButton,
	Stack,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import { useState } from "react";
import { ActionTypeTag } from "../utils/ActionTypeTag";
import { SourceCodeTag } from "../utils/SourceCodeTag";
import { AlertsList } from "./alerts/AlertsList";
import { HistoryDiagram } from "./history/HistoryDiagram";
import { RollsetCard } from "./rollset_cards/RollsetCard";
import { StandTypeMismatchCard } from "./stand_type_mismatch/StandTypeMismatchCard";

const getStatusChip = (statusCode: string) => {
	const statusMap: Record<
		string,
		{ label: string; color: string; bgColor: string }
	> = {
		P: { label: "Pending", color: "#d97706", bgColor: "#fef3c7" },
		A: { label: "Acknowledged", color: "#059669", bgColor: "#d1fae5" },
		C: { label: "Hold", color: "#dc2626", bgColor: "#fee2e2" },
	};

	const status = statusMap[statusCode] || {
		label: statusCode,
		color: "#6b7280",
		bgColor: "#f3f4f6",
	};

	return (
		<Chip
			label={status.label}
			size="small"
			sx={{
				backgroundColor: status.bgColor,
				color: status.color,
				fontWeight: 600,
				fontSize: "0.7rem",
				height: 24,
				borderRadius: "6px",
				border: `1px solid ${alpha(status.color, 0.2)}`,
			}}
		/>
	);
};

export const CardHeader = ({
	label,
	backgroundColor,
	expanded,
	setExpanded,
}: {
	label: string;
	backgroundColor: string;
	expanded: boolean;
	setExpanded: (expanded: boolean) => void;
}) => {
	return (
		<Box
			onClick={() => setExpanded(!expanded)}
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				cursor: "pointer",
				userSelect: "none",
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				<Box
					sx={{
						width: 4,
						height: 20,
						borderRadius: 1,
						backgroundColor,
					}}
				/>
				<Typography
					variant="subtitle1"
					fontWeight={700}
					sx={{ color: "text.primary", fontSize: "0.95rem" }}
				>
					{label}
				</Typography>
			</Box>

			<IconButton
				size="small"
				sx={{
					transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
					transition: "transform 0.2s ease-in-out",
				}}
			>
				<ExpandMore />
			</IconButton>
		</Box>
	);
};

type Props = {
	notification: GroupedNotification | null;
};

export const NotificationDetails = ({ notification }: Props) => {
	const theme = useTheme();

	const [newRollsetsExpanded, setNewRollsetsExpanded] = useState(true);

	if (!notification) {
		return (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					width: "100%",
					gap: 1.5,
					color: "text.secondary",
				}}
			>
				<Typography sx={{ fontWeight: 500, fontSize: "0.9rem" }}>
					Select a notification to see the details
				</Typography>
			</Box>
		);
	}

	return (
		<>
			<HelmetDexteel
				title={
					notification != null
						? `${notification.Description} - Rollshop Notifications`
						: null
				}
			/>
			<Box
				p={2}
				sx={{
					height: "100%",
					overflow: "auto",
					backgroundColor: alpha(theme.palette.grey[500], 0.02),
				}}
			>
				<Grid2 container spacing={2}>
					<Grid2 size={{ xs: 12 }}>
						<Box
							sx={{
								display: "flex",
								alignItems: "flex-start",
								justifyContent: "space-between",
								gap: 2,
								pb: 1.5,
								borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
							}}
						>
							<Box sx={{ flex: 1 }}>
								<Typography
									variant="h6"
									fontWeight={700}
									fontSize="1.1rem"
									sx={{ lineHeight: 1.3, mb: 1 }}
								>
									{notification.Description || "Notification"}
								</Typography>
								<Box
									sx={{
										display: "flex",
										gap: 1,
										alignItems: "center",
										flexWrap: "wrap",
									}}
								>
									<ActionTypeTag code={notification.ActionTypeCode} showName />
									<SourceCodeTag code={notification.SourceCode} />
								</Box>
							</Box>
							{getStatusChip(notification.StatusCode)}
						</Box>
					</Grid2>

					{notification.standTypeMismatch && (
						<StandTypeMismatchCard mismatch={notification.standTypeMismatch} />
					)}

					{/* Rollset Cards */}
					{notification.ActionTypeCode !== "T" && (
						<Card
							elevation={0}
							sx={{
								backgroundColor: "background.paper",
								borderRadius: 2.5,
								border: `1px solid`,
								borderColor: alpha("#6366f1", 0.15),
								overflow: "hidden",
								width: "100%",
							}}
						>
							<CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
								<CardHeader
									label={
										notification.targetRollsets.length === 0
											? "No rollset created"
											: `New rollset${
													notification.targetRollsets.length > 1 ? "s" : ""
												} (${notification.targetRollsets.length})`
									}
									backgroundColor="#6366f1"
									expanded={newRollsetsExpanded}
									setExpanded={setNewRollsetsExpanded}
								/>
								{notification.targetRollsets.length === 0 ? (
									<Stack spacing={1.5} mt={2} />
								) : (
									<Collapse in={newRollsetsExpanded} timeout="auto">
										<Stack spacing={1.5} mt={2}>
											{notification.targetRollsets.map((rollset) => (
												<RollsetCard
													key={rollset.NotificationRollsetId}
													rollset={rollset}
												/>
											))}
										</Stack>
									</Collapse>
								)}
							</CardContent>
						</Card>
					)}

					{/* Alerts Section */}
					<Grid2 size={{ xs: 12 }}>
						<AlertsList alerts={notification.alerts} />
					</Grid2>

					{/* History Section */}
					{(notification.brokenRollsets.length > 0 ||
						notification.targetRollsets.length > 0) && (
						<Grid2 size={{ xs: 12 }}>
							<HistoryDiagram
								targetRollsets={notification.targetRollsets}
								brokenRollsets={notification.brokenRollsets}
								alerts={notification.alerts}
							/>
						</Grid2>
					)}

					{/* Acknowledgment Info */}
					{notification.AcknowledgedBy && (
						<Grid2 size={{ xs: 12 }}>
							<Box
								sx={{
									p: 1.5,
									backgroundColor: alpha("#10b981", 0.05),
									borderRadius: 2,
									border: `1px solid ${alpha("#10b981", 0.15)}`,
								}}
							>
								<Typography variant="caption" color="text.secondary">
									Acknowledged by{" "}
									<Typography component="span" fontWeight={600}>
										{notification.AcknowledgedBy}
									</Typography>
									{notification.AcknowledgedAt && (
										<>
											{" "}
											on{" "}
											<Typography component="span" fontWeight={600}>
												{new Date(notification.AcknowledgedAt).toLocaleString()}
											</Typography>
										</>
									)}
								</Typography>
								{notification.Comments && (
									<Typography
										variant="body2"
										sx={{
											mt: 0.5,
											fontStyle: "italic",
											color: "text.secondary",
										}}
									>
										"{notification.Comments}"
									</Typography>
								)}
							</Box>
						</Grid2>
					)}
				</Grid2>
			</Box>
		</>
	);
};
